import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import {
  IEmailTemplate,
  IEmailTemplatePreview,
  previewEmailTemplate,
} from "../../../Api/emailTemplatesApi";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { useNotifier } from "../../../Core/Notifier";

interface Props {
  template: IEmailTemplate;
  onClose: () => void;
}

// Standalone preview (invoked from the list "eye" icon). Renders
// through the backend preview endpoint so tokens resolve exactly the
// way the sender will see them at compose-time. Falls back to the
// stored body if the preview call fails.
const PreviewDialog: React.FC<Props> = ({ template, onClose }) => {
  const { showErrorMessage } = useNotifier();
  const [busy, setBusy] = useState(true);
  const [preview, setPreview] = useState<IEmailTemplatePreview | null>(null);
  const [mode, setMode] = useState<"desktop" | "mobile">("desktop");

  useEffect(() => {
    let alive = true;
    previewEmailTemplate(template.id)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => showErrorMessage(e?.message || "Failed to render preview."))
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [template.id, showErrorMessage]);

  const isSms = template.template_type === "sms";
  const subject = preview?.subject ?? template.subject;
  const body    = preview?.body    ?? template.body;
  const isHtml  = /<[a-z][\s\S]*>/i.test(body);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${B13.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, bgcolor: tint12(B13.primary),
          color: B13.primary, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {isSms ? <SmsOutlinedIcon /> : <DesktopMacIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...B13TypeScale.h3 }} noWrap>{template.name}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
            <Chip size="small" label={template.template_type_label || template.template_type}
              sx={{ height: 20, fontSize: 10.5, bgcolor: tint12(B13.primary), color: B13.primary, fontWeight: 700 }} />
            {template.category_label && (
              <Chip size="small" label={template.category_label}
                sx={{ height: 20, fontSize: 10.5, bgcolor: "#F1F5F9", color: B13Ink.ink2 }} />
            )}
          </Stack>
        </Box>
        {!isSms && (
          <Stack direction="row" spacing={0.5} sx={{ bgcolor: "#F1F5F9", borderRadius: "8px", p: 0.25 }}>
            {(["desktop", "mobile"] as const).map((m) => {
              const active = mode === m;
              return (
                <IconButton
                  key={m}
                  size="small"
                  onClick={() => setMode(m)}
                  aria-pressed={active}
                  sx={{
                    bgcolor: active ? "#FFFFFF" : "transparent",
                    color: active ? B13.primary : B13Ink.ink2,
                    borderRadius: "6px", width: 30, height: 26,
                  }}
                >
                  {m === "desktop" ? <DesktopMacIcon fontSize="small" /> : <PhoneIphoneIcon fontSize="small" />}
                </IconButton>
              );
            })}
          </Stack>
        )}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#F7F9FC", p: 3 }}>
        {busy ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isSms ? (
          <Box sx={{ mx: "auto", width: 320 }}>
            <Box sx={{ borderRadius: 4, bgcolor: "#F1F5F9", p: 2, minHeight: 180, border: `1px solid ${B13.line}` }}>
              <Box sx={{
                display: "inline-block", bgcolor: "#FFFFFF",
                borderRadius: "16px", px: 1.5, py: 1,
                fontSize: 13.5, lineHeight: 1.4,
                boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
                maxWidth: "100%", wordWrap: "break-word", whiteSpace: "pre-wrap",
              }}>
                {body || "(empty message)"}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            mx: "auto",
            width: mode === "mobile" ? 360 : "100%",
            maxWidth: "100%",
            border: `1px solid ${B13.line}`, borderRadius: 3, overflow: "hidden",
            bgcolor: "#FFFFFF",
          }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#F8FAFC", borderBottom: `1px solid ${B13.line}` }}>
              <Typography sx={{ fontSize: 11, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
                Subject
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: B13.ink, mt: 0.25 }}>
                {subject || "(no subject)"}
              </Typography>
            </Box>
            {isHtml ? (
              <Box
                sx={{ p: 3, fontSize: 14, lineHeight: 1.6, color: B13.ink, minHeight: 260 }}
                dangerouslySetInnerHTML={{ __html: body }}
              />
            ) : (
              <Box sx={{ p: 3, fontSize: 14, lineHeight: 1.6, color: B13.ink, whiteSpace: "pre-wrap", minHeight: 260 }}>
                {body || "(no body)"}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDialog;
